import React, { useState, useEffect } from "react";
import type { HeadFC } from "gatsby";
import { CloudArrowUpIcon, LockClosedIcon, ServerIcon } from '@heroicons/react/20/solid';
import Seo from '../components/seo';
import NavBar from '../components/navbar';
import Footer from '../components/footer';
import SectionHeader from '../components/sectionHeader';
import ExtraLifeDonationsTracker from "../components/extraLifeDonationsTracker";
import { IExtraLife, initialExtraLifeInformation, getExtraLifeInformation } from "../utilities/api";
import CustomBreadcrumb from "../components/customBreadcrumb";

const ExtraLife: React.FC = () => {

    const [extraLifeInformation, setExtraLifeInformation] = useState<IExtraLife>(initialExtraLifeInformation);

    const channelName = "NetizenKing"

    const howItWorksBulletPoints = [
        {
            description:
                "Extra Life is a 24-hour fundraising and gaming marathon to support Children's Miracle Network Hospitals.🏥",
        },
        {
            description:
                "🎮 On Game Day, I will join thousands of gamers who each dedicate a full 24hrs to play games and raise funds through donations from friends, family and community members like YOU! 💗",
        },
        {
            description:
                "100% of the donations go to my local Children's Hospital. 🏥 ",
        },
        {
            description:
                "Donations are tax deductible! 👍",
        },
        {
            description:
                "TOGETHER we can make a difference for sick kids! 💗",
        },
    ];

    const incentivesBulletPoints = [
        {
            description:
                "Games 🕹️",
        },
        {
            description:
                "Stickers 🦄",
        },
        {
            description:
                "Tees 👕",
        },
        {
            description:
                "Silver Medals 🥈",
        },
        {
            description:
                "Gold Medals 🥇",
        },
        {
            description:
                "✨ PLUS EVEN MORE INCENTIVES!✨",
        },
    ];

    const whyIExtraLifeDescription = "As you may know, this is a cause very close to my heart! After watching my best friend, Lance, live with brain cancer and ultimately lose his battle at the age of 13, I now want to do whatever I can to support other children going through similar experiences."

    const startingTimeTitle = "Starting on November 9th, 9am PST"
    const statingTimeDescription = "I'll be starting my eighth annual 24 Hour Gaming Marathon to raise funds for sick kids at our Local Children's Hospital! Last year we raised $2736.27! In 2023 we're going to do our best to raise even more!"

    const crumbs = [
        { crumbLabel: 'Extra Life', pathName: '/extra-life' }
    ];

    useEffect(() => {
        if (extraLifeInformation !== initialExtraLifeInformation) return;

        getExtraLifeInformation().then((data) => {
            if (extraLifeInformation !== initialExtraLifeInformation) return;

            setExtraLifeInformation({ ...data });
        });
    }, [extraLifeInformation, setExtraLifeInformation]);



    return (
        <div className="isolate website-bg">
            <NavBar />
            <main className="mx-auto max-w-6xl px-2 sm:px-6 md:px-8">
                <CustomBreadcrumb crumbs={crumbs} />
                {/* SECTION: PAGE TITLE */}
                <div className="mt-7 md:mt-14 lg:mt-20">
                    <SectionHeader title={'Extra Life'} description={"Play games, fundraise, and together, we can Change Kids' Health to Change the Future!"} />
                </div>

                {/* SECTION: SHOWS TWITCH LIVE STREAM IF LIVE */}
                {extraLifeInformation.streamIsLive && (<div className="flex flex-row justify-center items-center mt-10 md:mt-10 lg:mt-20">
                    <div className='relative aspect-w-16 w-full aspect-h-9'>
                        <iframe
                            className="border-0 absolute inset-0"
                            src={`https://player.twitch.tv/?channel=${channelName}&parent=localhost&parent=jkt-website.vercel.app&parent=netizenk.ing`}
                            height="100%"
                            width="100%"
                            allow="fullscreen;"
                        >
                        </iframe>
                    </div>
                    <div className='relative aspect-w-16 w-2/5 aspect-h-9 hidden lg:block'>
                        <iframe
                            className="border-0 absolute inset-0"
                            src={`https://www.twitch.tv/embed/${channelName}/chat?parent=localhost&parent=jkt-website.vercel.app&parent=netizenk.ingh`}
                            height="100%"
                            width="100%"
                        >
                        </iframe>
                    </div>
                </div>)}

                {/* SECTION: Why I Extra Life ? */}
                <div className="flex flex-col md:flex-row mt-20">

                    <div className="flex flex-col justify-center items-center w-full px-10 mb-7 md:mb-0 md:w-1/2">
                        <p className="text-center mt-2 text-xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                            {"Why I Extra Life ?"}
                        </p>
                        <p className="text-center mx-auto mt-6 max-w-2xl text-md leading-8 text-gray-400">
                            {whyIExtraLifeDescription}
                        </p>
                    </div >

                    <div className='flex w-full md:w-1/2 pr-0 md:pr-8'>
                        <div className="aspect-w-16 aspect-h-9 w-full">
                            <iframe
                                className="border-0 absolute inset-0"
                                src={"https://www.youtube.com/embed/DZye5cGGkNE"}
                                title="YouTube video player"
                                allow="fullscreen;"
                            >
                            </iframe>
                        </div>
                    </div>
                </div>

                <div className="mt-7 md:mt-14 lg:mt-20">
                    <SectionHeader title={startingTimeTitle} description={statingTimeDescription} />
                </div>

                {/* SECTION: DONATION MILESTONE TRACKER*/}
                <ExtraLifeDonationsTracker extraLifeInformation={extraLifeInformation} />

                {/* SECTION: HOW IT WORKS */}
                <div className="overflow-hidden py-12 sm:py-20">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8">
                        <div className="mx-auto grid max-w-2xl grid-cols-1 gap-y-16 gap-x-8 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2">
                            {/* LEFT SIDE: How it Works */}
                            <div className="lg:pr-8 lg:pt-4">
                                <div className="lg:max-w-lg">
                                    <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">How it Works</p>
                                    <dl className="mt-10 max-w-xl space-y-8 text-base leading-7 text-gray-400 lg:max-w-none">
                                        {howItWorksBulletPoints.map((bulletPoint, index) => (
                                            <div key={index} className="relative pl-0">
                                                <dd className="inline">{"→ " + bulletPoint.description}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            </div>
                            {/* RIGHT SIDE: INCENTIVES */}
                            <div className="lg:pr-8 lg:pt-4">
                                <div className="lg:max-w-lg">
                                    <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">Donation Incentives include</p>
                                    <dl className="mt-10 max-w-xl space-y-8 text-base leading-7 text-gray-400 lg:max-w-none">
                                        {incentivesBulletPoints.map((bulletPoint, index) => (
                                            <div key={index} className="relative pl-0">
                                                <dd className="inline">{"→ " + bulletPoint.description}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </main>
            <footer>
                <Footer />
            </footer>
        </div>
    );
};

export default ExtraLife;


export const Head: HeadFC = () => <Seo title="Extra Life" />;
